import React, { useEffect, useState } from 'react';
import {
  Stack,
  Typography,
  Button,
  Card,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../../../context/AuthContext/AuthContext';

import getRfxSyncStatus from '../../services/get/rfxSyncStatus/getRfxSyncStatus';
import getRfxTradingAccounts from '../../services/get/rfxTradingAccounts/getRfxTradingAccounts';

import { RfxTradingAccountSummaryDTO } from '../../services/get/rfxTradingAccounts/models';

import { headerSx, rootSx, titleSx } from '../styles';

type InternalStep = 'loading' | 'unsynced' | 'synced';

interface RfxWithdrawStepProps {
  selectedAccountId: number | null;
  setSelectedAccountId: (id: number | null) => void;
  setSelectedAccountNumber: (accountNumber: string | null) => void;
  onCloseModal?: () => void;
}

const RfxWithdrawStep: React.FC<RfxWithdrawStepProps> = ({
  selectedAccountId,
  setSelectedAccountId,
  setSelectedAccountNumber,
  onCloseModal,
}) => {
  const { t } = useTranslation('withdraw');
  const { tokens } = useAuth();
  const navigate = useNavigate();

  const [internalStep, setInternalStep] = useState<InternalStep>('loading');
  const [tradingAccounts, setTradingAccounts] = useState<RfxTradingAccountSummaryDTO[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checkSyncStatus = async () => {
    if (!tokens?.accessToken) return;

    setInternalStep('loading');
    setError(null);

    try {
      const response = await getRfxSyncStatus(tokens.accessToken);

      if (response.status === 'success' && response.data) {
        if (response.data.syncState === 'SYNCED') {
          setInternalStep('synced');
          fetchTradingAccounts();
        } else {
          setInternalStep('unsynced');
        }
      } else {
        setError('UNKNOWN_ERROR');
        setInternalStep('unsynced');
      }
    } catch (err: any) {
      setError(err.message || 'UNKNOWN_ERROR');
      setInternalStep('unsynced');
    }
  };

  const fetchTradingAccounts = async () => {
    if (!tokens?.accessToken) return;

    setAccountsLoading(true);
    setError(null);

    try {
      const response = await getRfxTradingAccounts(tokens.accessToken);

      if (response.status === 'success' && response.data) {
        setTradingAccounts(response.data.accounts);
      } else {
        setError('UNKNOWN_ERROR');
      }
    } catch (err: any) {
      setError(err.message || 'UNKNOWN_ERROR');
    } finally {
      setAccountsLoading(false);
    }
  };

  useEffect(() => {
    checkSyncStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokens?.accessToken]);

  if (internalStep === 'loading') {
    return (
      <Stack sx={rootSx} alignItems="center" justifyContent="center">
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>{t('RFX_WITHDRAW.STEP_SELECT_ACCOUNT.LOADING')}</Typography>
      </Stack>
    );
  }

  if (internalStep === 'unsynced') {
    return (
      <Stack sx={rootSx}>
        <Stack sx={headerSx}>
          <Typography sx={titleSx}>{t('RFX_WITHDRAW.STEP_SYNC.TITLE')}</Typography>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{t(`RFX_WITHDRAW.ERRORS.${error}`, { defaultValue: t('RFX_WITHDRAW.ERRORS.UNKNOWN_ERROR') })}</Alert>}

        <Typography sx={{ mb: 3 }}>
          {t('RFX_WITHDRAW.STEP_SYNC.DESCRIPTION')}
        </Typography>

        <Button
          variant="contained"
          fullWidth
          onClick={() => {
            onCloseModal?.();
            navigate('/switch-rfx?startSync=true');
          }}
        >
          {t('RFX_WITHDRAW.STEP_SYNC.SYNC_BUTTON')}
        </Button>
      </Stack>
    );
  }

  // Render synced state - show trading accounts
  return (
    <Stack sx={rootSx}>
      <Stack sx={headerSx}>
        <Typography sx={titleSx}>{t('RFX_WITHDRAW.STEP_SELECT_ACCOUNT.TITLE')}</Typography>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{t(`RFX_WITHDRAW.ERRORS.${error}`, { defaultValue: t('RFX_WITHDRAW.ERRORS.UNKNOWN_ERROR') })}</Alert>}

      {accountsLoading ? (
        <Stack alignItems="center" py={4}>
          <CircularProgress />
        </Stack>
      ) : tradingAccounts.length === 0 ? (
        <Typography>{t('RFX_WITHDRAW.STEP_SELECT_ACCOUNT.NO_ACCOUNTS')}</Typography>
      ) : (
        <Stack spacing={2}>
          {tradingAccounts.map((account) => (
            <Card
              key={account.accountId}
              onClick={() => {
                setSelectedAccountId(account.accountId);
                setSelectedAccountNumber(account.accountNumber);
              }}
              sx={{
                p: 2,
                cursor: 'pointer',
                border: selectedAccountId === account.accountId
                  ? '2px solid #99A8FF'
                  : '2px solid transparent',
                backgroundColor: selectedAccountId === account.accountId
                  ? '#99A8FF1A'
                  : '#1E1F25',
                '&:hover': {
                  backgroundColor: '#99A8FF1A',
                },
              }}
            >
              <Typography variant="subtitle1" fontWeight="bold">
                {account.displayName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {account.accountNumber} • {account.platform} • {account.currency}
              </Typography>
              <Typography variant="body2">
                Balance: {account.balance} {account.currency}
              </Typography>
            </Card>
          ))}
        </Stack>
      )}
    </Stack>
  );
};

export default RfxWithdrawStep;
